<template>
  <div class="card">
    <div class="body">
      <validation-observer
        ref="form"
        v-if="!finished"
      >
        <form @submit.prevent="submitForm">
          <form-wizard
            class="wizard"
            step-size="sm"
            title=""
            subtitle=""
            next-button-text="Avançar"
            back-button-text="Voltar"
            finish-button-text="Salvar"
            :start-index.sync="step"
          >
            <tab-content
              class="tab tab-step1"
              title="Consulta"
              :before-change="validateForm"
            >
              <template v-if="step === 0">
                <error-cant-fetch v-if="errors.cantFetch"></error-cant-fetch>
                <step-consultations v-model="form" v-else/>
              </template>
            </tab-content>
            <tab-content
              class="tab tab-step2"
              title="Opções da consulta"
              :before-change="validateForm"
            >
              <step-information :tag="tag" v-model="form" v-if="step === 1"/>
            </tab-content>
            <tab-content
              class="tab tab-step3"
              title="Finalizar"
              :before-change="validateForm"
            >
              <template v-if="step === 2">
                <div class="error-cant-create alert -danger" v-if="errors.unprocessableEntityWhenCreate">
                    <template>Ops!</template>

                    <div>
                        <ul class="errors">
                            <li v-for="error in errors.unprocessableEntityWhenCreate.errors" :key="error">{{ error[0] }}</li>
                        </ul>
                    </div>
                </div>

                <step-finish :tag="tag" v-model="form"/>
              </template>
            </tab-content>

            <template slot="footer" slot-scope="props">
              <div class="wizard-footer-buttons" v-if="!errors.cantFetch">
                <wizard-button
                  @click.native="props.prevTab()"
                  :style="props.fillButtonStyle"
                  :disabled="props.activeTabIndex === 0"
                  class="prev"
                >Voltar</wizard-button>
                <wizard-button
                  @click.native="backTolist"
                  :style="props.fillButtonStyle"
                  :disabled="props.activeTabIndex !== 0"
                  class="prev"
                >Voltar</wizard-button>
                <wizard-button
                  v-if="!props.isLastStep"
                  @click.native="props.nextTab()"
                  :style="props.fillButtonStyle"
                  class="next"
                >Avançar</wizard-button>
                <wizard-button
                  v-else
                  @click.native="createQuery()"
                  :style="props.fillButtonStyle"
                  class="finish"
                >Salvar</wizard-button>
              </div>
            </template>
          </form-wizard>
        </form>
      </validation-observer>

      <loader v-if="loading"/>
    </div>
  </div>
</template>

<script>
import Loader from '@/support/components/Loader.vue';
import currency from '@/support/filters/currency';
import StepConsultations from '@/template/components/new-query/StepConsultations.vue';
import StepFinish from '@/template/components/new-query/StepFinish.vue';
import StepInformation from '@/template/components/new-query/StepInformation.vue';
import ErrorCantFetch from '@/template/components/new-query/messages/ErrorCantFetch.vue';
import newQueryFormFields from '@/template/mixins/newQueryFormFields';
import { ValidationObserver } from 'vee-validate';
import { FormWizard, TabContent, WizardButton } from 'vue-form-wizard';
import { mapState } from 'vuex';

export default {
  name: 'NewQuery',

  props: {
    tag: {
      type: String,
      required: true,
    },
    consultationsRoute: {
      required: true,
      type: String,
    },
    template: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      loading: false,
      finished: false,
      step: 0,
      errors: {
        cantFetch: false,
        unprocessableEntityWhenCreate: false,
        paymentRequiredWhenCreate: false,
        cantCreate: false,
      },
      form: {
        consultation: [],
      },
    };
  },

  methods: {
    /**
     * Load all available compliance.
     *
     * @returns {Promise<void>}
     */
    async loadAvailableConsultations() {
      try {
        this.resetErrors();
        this.loading = true;

        await this.$store.dispatch('compliance/fetchAvailableConsultations', { tag: this.tag });
      } catch {
        this.errors.cantFetch = true;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reset the errors.
     */
    resetErrors() {
      this.errors.cantFetch = false;
      this.errors.unprocessableEntityWhenCreate = false;
      this.errors.paymentRequiredWhenCreate = false;
      this.errors.cantCreate = false;
    },

    /**
     * Validate the form.
     *
     * @returns {Promise<boolean>}
     */
    async validateForm() {
      this.resetErrors();
      return !!(await this.$refs.form.validate());
    },

    backTolist() {
      this.$router.back();
    },

    /**
     * Create the consultation.
     *
     * @returns {Promise<void>}
     */
    async createQuery() {
      try {
        this.loading = true;
        this.resetErrors();

        const formattedConsultation = this.formatConsultation();

        await this.$store.dispatch('template/addInTemplate', { consultation: formattedConsultation, id: this.$route.params.id });
        this.$notify({
          group: 'app',
          type: 'success',
          title: 'Sucesso!',
          text: 'Consulta adicionada com sucesso.',
        });
        await this.$router.push({
          name: {
            cpf: 'Template-Physical-Person-Edit',
            cnpj: 'Template-Legal-Person-Edit',
            vehicles: 'Template-Vehicle-Edit',
            properties: 'Template-Property-Edit',
          }[this.tag] || 'Template-Other-Edit',
          params: {
            id: this.$route.params.id,
          },
        });
      } catch (error) {
        this.errors.cantCreate = true;

        if (error.response && error.response.status === 422) {
          this.errors.unprocessableEntityWhenCreate = error.response.data;
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Reset the new compliance.
     */
    resetNewQuery() {
      this.form.consultation = {};

      Object.keys(this.form)
        .filter((field) => field !== 'consultation')
        .forEach((field) => {
          delete this.form[field];
        });

      this.resetErrors();
      this.step = 0;
      this.finished = false;
    },

    submitForm() {
    },

    humanizeFromOptions(value, options) {
      if (!options) {
        return value;
      }

      return options
        .find((option) => option.value === value || parseInt(option.value, 10) === parseInt(value, 10))
        .label;
    },

    formatConsultation() {
      const formattedConsultation = {
        name: this.form.consultation.name,
        label: this.form.consultation.label,
        fields: [],
      };

      if (this.exclusiveFieldsGrouped.length === 0) {
        return formattedConsultation;
      }

      formattedConsultation.fields = this.exclusiveFieldsGrouped[0].fields.reduce((array, field) => {
        if (this.form[field.name]) {
          array.push({
            label: `${field.label} (${this.humanizeFromOptions(this.form[field.name], field.values)})`,
            name: field.name,
            value: this.form[field.name],
          });
        }

        return array;
      }, []);

      return formattedConsultation;
    },
  },

  computed: {
    ...mapState('auth', [
      'user',
    ]),
  },

  mixins: [
    newQueryFormFields,
  ],

  mounted() {
    this.loadAvailableConsultations();
  },

  filters: {
    currency,
  },

  components: {
    FormWizard,
    TabContent,
    WizardButton,
    ValidationObserver,
    Loader,
    ErrorCantFetch,
    StepConsultations,
    StepInformation,
    StepFinish,
  },
};
</script>

<style lang="scss" scoped>
  .wizard {
    .wizard-footer-header {
      @apply text-black;
      @apply text-xs;
      @apply italic;
      @apply mt-2;
    }

    .tab {
      &:not(.tab--only-show-error) {
        padding-bottom: 2em;
      }
    }
  }
  .vue-form-wizard .wizard-card-footer .wizard-footer-header,
  .vue-form-wizard .wizard-card-footer .wizard-footer-buttons {
    width: 100%;
  }
</style>
